import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { 
  FaBell, 
  FaEnvelope, 
  FaQuestionCircle,
  FaChevronDown,
  FaMoon,
  FaSun,
    FaUserCircle,
    FaSignOutAlt
} from 'react-icons/fa';

interface HeaderProps {
  onMenuClick: () => void;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const { currentUser, logout, userProfile } = useAuth();
  const navigate = useNavigate();
  const [darkMode, setDarkMode] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const notifications = [
    { id: 1, title: 'New user registered', time: '5 min ago', unread: true },
    { id: 2, title: 'System backup completed', time: '1 hour ago', unread: true },
    { id: 3, title: 'Weekly report ready', time: '2 hours ago', unread: false },
  ];

  const unreadCount = notifications.filter(n => n.unread).length;

  const handleSignOut = async () => {
    try {
      await logout();
      navigate('/'); // navigate to home
    } catch (err) {
      console.error('Logout failed', err);
    }
  };

  const firstNameRaw = userProfile?.firstName || currentUser?.displayName?.split(' ')[0] || currentUser?.email?.split('@')[0] || 'Admin';
  const firstName = firstNameRaw
    ? firstNameRaw.charAt(0).toUpperCase() + String(firstNameRaw).slice(1)
    : 'Admin';

  return (
    <header className="sticky top-0 z-40 border-b border-slate-200/80 bg-white/90 shadow-[0_1px_10px_rgba(15,23,42,0.04)] backdrop-blur-xl">
      <div className="px-3 py-2.5 lg:px-4">
        <div className="flex items-center justify-between">
          {/* Left side */}
          <div className="flex items-center space-x-3">
            <button
              onClick={onMenuClick}
              className="lg:hidden p-2 rounded-md hover:bg-slate-100 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            
            <div className="hidden md:block">
              <h2 className="text-sm font-semibold text-slate-900">Welcome, {firstName}</h2>
              <p className="text-xs text-slate-500">Admin workspace</p>
            </div>
          </div>

          {/* Right side */}
          <div className="flex items-center space-x-1.5">
            {/* Theme Toggle */}
            <button
              onClick={() => setDarkMode(!darkMode)}
              className="p-2 rounded-md hover:bg-slate-100 transition-colors"
              title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {darkMode ? (
                <FaSun className="h-4 w-4 text-slate-600" />
              ) : (
                <FaMoon className="h-4 w-4 text-slate-600" />
              )}
            </button>

            {/* Help */}
            <button className="p-2 rounded-md hover:bg-slate-100 transition-colors" title="Help">
              <FaQuestionCircle className="h-4 w-4 text-slate-600" />
            </button>

            {/* Messages */}
            <button className="relative p-2 rounded-md hover:bg-slate-100 transition-colors" title="Messages">
              <FaEnvelope className="h-4 w-4 text-slate-600" />
              <span className="absolute -top-0.5 -right-0.5 h-3.5 min-w-3.5 rounded-full bg-rose-500 px-1 text-[9px] leading-3.5 text-white flex items-center justify-center">
                3
              </span>
            </button>

            {/* Notifications */}
            <div className="relative">
              <button
                onClick={() => setNotificationsOpen(!notificationsOpen)}
                className="relative p-2 rounded-md hover:bg-slate-100 transition-colors"
                title="Notifications"
              >
                <FaBell className="h-4 w-4 text-slate-600" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 h-3.5 min-w-3.5 rounded-full bg-rose-500 px-1 text-[9px] leading-3.5 text-white flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </button>

              {/* Notifications Dropdown */}
              {notificationsOpen && (
                <div className="absolute right-0 mt-2 w-72 overflow-hidden rounded-lg border border-slate-200 bg-white py-1.5 shadow-2xl shadow-slate-900/10 z-50">
                  <div className="px-3 py-2 border-b border-slate-100">
                    <h3 className="text-sm font-semibold text-slate-900">Notifications</h3>
                    <p className="text-xs text-slate-500">{unreadCount} unread messages</p>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.map((notification) => (
                      <div
                        key={notification.id}
                        className={`px-3 py-2 hover:bg-slate-50 ${notification.unread ? 'bg-sky-50' : ''}`}
                      >
                        <div className="flex justify-between items-start">
                          <p className="text-xs font-medium text-slate-800">{notification.title}</p>
                          {notification.unread && (
                            <span className="h-1.5 w-1.5 rounded-full bg-sky-500"></span>
                          )}
                        </div>
                        <p className="text-[11px] text-slate-500 mt-0.5">{notification.time}</p>
                      </div>
                    ))}
                  </div>
                  <div className="px-3 py-2 border-t border-slate-100">
                    <button className="text-xs text-primary-700 hover:text-primary-800 font-medium">
                      View all notifications
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center space-x-2 rounded-md p-1.5 hover:bg-slate-100 transition-colors"
              >
                <div className="h-8 w-8 rounded-md bg-gradient-to-br from-primary-600 to-primary-800 flex items-center justify-center text-white text-sm font-semibold shadow-sm">
                  {firstName.charAt(0).toUpperCase()}
                </div>
                <div className="hidden md:block text-left">
                  <p className="max-w-44 truncate text-xs font-semibold text-slate-800">{currentUser?.email || 'Admin'}</p>
                  <p className="text-[11px] text-slate-500 capitalize">{currentUser?.role || 'Administrator'}</p>
                </div>
                <FaChevronDown className={`h-3 w-3 text-slate-500 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* User Dropdown */}
              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-56 overflow-hidden rounded-lg border border-slate-200 bg-white py-1.5 shadow-2xl shadow-slate-900/10 z-50">
                  <div className="px-3 py-2.5 border-b border-slate-100">
                    <p className="truncate text-sm font-semibold text-slate-900">{userProfile?.firstName ? `${userProfile.firstName} ${userProfile.lastName || ''}`.trim() : (currentUser?.displayName || currentUser?.email)}</p>
                    <p className="truncate text-xs text-slate-500">{userProfile?.email || currentUser?.email}</p>
                  </div>
                  <div className="py-1">
                    <Link to="/user/profile" onClick={() => setUserMenuOpen(false)} className="flex items-center px-3 py-2 text-xs text-slate-700 hover:bg-slate-50">
                      <FaUserCircle className="h-3.5 w-3.5 mr-2.5 text-slate-500" />
                      Your Profile
                    </Link>
                   
                  </div>
                  <div className="border-t border-slate-100 pt-1">
                    <button onClick={handleSignOut} className="w-full flex items-center px-3 py-2 text-xs text-rose-600 hover:bg-rose-50">
                      <FaSignOutAlt className="h-3.5 w-3.5 mr-2.5" />
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Breadcrumbs */}
        {/* <div className="mt-4 flex items-center text-sm text-gray-600">
          <a href="/admin/library" className="hover:text-primary-600">Dashboard</a>
          <span className="mx-2">/</span>
          <span className="text-gray-800 font-medium">Current Page</span>
        </div> */}
      </div>
    </header>
  );
};

export default Header;
